import * as Yup from 'yup';
import Moment from 'moment';
import { useCowbellTranslations } from '../../../../i18n/translations';
import {
  noOfEmployees,
  revenue,
} from '../../../../utils/validationSchemas/accounts.schema';

/**
 * @param usesSicClassification whether this account's market classifies by SIC
 *   rather than NAICS. The required industry code is then `sicCode`: a UK or
 *   Australian account is not required to carry a NAICS code, and requiring one
 *   blocked the save and inflated the panel's missing-field count for accounts
 *   that only ever had a SIC code on file.
 */
export const useAccountDetailsSchema = (usesSicClassification = false) => {
  const { t } = useCowbellTranslations();

  // Only the market's own code is declared. Declaring both and requiring one
  // would leave the other in the resolver's cast output, putting an empty
  // `sicCode` into the payload of every US account.
  const industryCodeShape: Record<string, Yup.StringSchema> = {
    [usesSicClassification ? 'sicCode' : 'naicsCode']: Yup.string()
      .label('Industry Code')
      .required(),
  };

  return Yup.object().shape({
    name: Yup.string().trim().required().label('Name'),
    ...industryCodeShape,
    revenue,
    noOfEmployeesAll: noOfEmployees,
    yearEstablished: Yup.number()
      .label('Year Established')
      .lessThan(
        Moment().add(1, 'y').year(),
        '${label} must be less than or equal to current year'
      )
      .moreThan(1499, '${label} must be greater than or equal to 1500')
      .required()
      .typeError('${label} is a required field'),
    ownershipType: Yup.string()
      .label(t('account:ownershipType.label'))
      .required(),
  });
};
